/**
 * Criador de publicações — tela completa, do começo ao fim.
 *
 * Modelado no criador de publicações da PRODESP (stepper numerado, prévia
 * viva em tamanho real de papel sobre a "mesa", validação por severidade,
 * envio ao fluxo de aprovação), reconstruído do zero no padrão EGBA: as
 * cores da plataforma, a máquina de estados de `intake/estados.ts` e a
 * mesma métrica de composição que a tabela de preços cobra.
 *
 * Passos: 1 Matéria → 2 Conteúdo (editor + prévia diagramada) → 3 Revisão e
 * envio. O pedido nasce rascunho no passo 1→2 (o servidor reaproveita
 * rascunho aberto — dois cliques não consomem dois protocolos); "Enviar para
 * análise" dispara a transição real da máquina de estados.
 *
 * Categoria com `forma_intake` de planilha (escolas) cria o rascunho e segue
 * para a ficha do pedido, onde o upload já existe — este criador diagrama
 * demonstrações; não duplica o fluxo de planilha.
 */

import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import type { Demonstracao } from '@egba-do/shared'
import { estimarComposicao, validarConjunto } from '@egba-do/shared'
import { api, urlMidia, type Item, type Lista } from '../api/cliente'
import { EditorDemonstracoes } from '../componentes/intake/EditorDemonstracoes'
import { PreviaDiario } from '../componentes/intake/PreviaDiario'
import { AreaTexto, Aviso, Botao, Carregando, Cartao, Selecao, Vazio } from '../componentes/ui'
import { podeAdministrar, useAuth } from '../estado/auth'

interface Categoria {
  id: string
  nome: string
  descricao: string | null
  formaIntake: string
  fundamentoLegal: string | null
}

interface PedidoCriado {
  id: string
  protocolo: string | null
  dados?: { demonstracoes?: Demonstracao[] } | null
}

interface OrganizacaoResumo {
  id: string
  razaoSocial: string
  cnpj: string | null
  logoUrl?: string | null
}

interface Capa {
  imagemUrl?: string | null
  titulo?: string
  subtitulo?: string
  grafico?: boolean
}

const PASSOS = ['Matéria', 'Conteúdo', 'Revisão e envio'] as const

function Stepper({ atual }: { atual: number }) {
  return (
    <ol className="flex items-center gap-2">
      {PASSOS.map((nome, i) => {
        const feito = i < atual
        const ativo = i === atual
        return (
          <li key={nome} className="flex items-center gap-2">
            {i > 0 && <span className={`h-0.5 w-8 rounded ${feito || ativo ? 'bg-marca-600' : 'bg-slate-200'}`} />}
            <span
              className={`flex size-7 items-center justify-center rounded-full text-xs font-semibold ${
                feito
                  ? 'bg-emerald-600 text-white'
                  : ativo
                    ? 'bg-marca-600 text-white'
                    : 'bg-slate-200 text-slate-500'
              }`}
            >
              {feito ? '✓' : i + 1}
            </span>
            <span className={`text-sm font-medium ${ativo ? 'text-slate-900' : 'text-slate-500'}`}>{nome}</span>
          </li>
        )
      })}
    </ol>
  )
}

export function PedidoNovo() {
  const { usuario } = useAuth()
  const admin = podeAdministrar(usuario?.papel)
  const navegar = useNavigate()

  const [passo, setPasso] = useState(0)
  const [categoriaId, setCategoriaId] = useState('')
  const [organizacaoId, setOrganizacaoId] = useState('')
  const [pedido, setPedido] = useState<PedidoCriado | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [conteudoSalvo, setConteudoSalvo] = useState(false)

  // Estado vivo para a prévia — alimentado pelo editor a cada tecla.
  const [previa, setPrevia] = useState<{ lista: Demonstracao[]; colunas: number } | null>(null)

  // Identidade & capa da peça (persistida em pedidos.dados.capa).
  const [capa, setCapa] = useState<Capa>({ grafico: false })
  const [descricaoCapa, setDescricaoCapa] = useState('')
  const [capaSalva, setCapaSalva] = useState(true)

  // Edição de um rascunho existente: /pedidos/novo?pedido=<id> retoma direto
  // no passo de conteúdo — é o "abrir no editor completo" da ficha.
  const [params] = useSearchParams()
  const pedidoParam = params.get('pedido')
  const { data: pedidoExistente } = useQuery({
    queryKey: ['pedido-editar', pedidoParam],
    queryFn: () =>
      api.get<Item<{ id: string; protocolo: string | null; status: string; categoria: { id: string } | null; organizacaoId?: string }>>(
        `/api/v1/pedidos/${pedidoParam}`,
      ),
    enabled: !!pedidoParam,
  })
  useEffect(() => {
    const d = pedidoExistente?.dados
    if (!d || pedido) return
    if (d.status !== 'rascunho') {
      navegar(`/pedidos?abrir=${d.id}`, { replace: true })
      return
    }
    if (d.categoria?.id) setCategoriaId(d.categoria.id)
    setPedido({ id: d.id, protocolo: d.protocolo })
    void api
      .get<Lista<Demonstracao> & { capa?: Capa | null }>(`/api/v1/pedidos/${d.id}/demonstracoes`)
      .then((r) => {
        const salvas = r.dados ?? []
        setConteudoInicial(salvas)
        setVersaoConteudo((v) => v + 1)
        if (salvas.length) {
          setPrevia({ lista: salvas, colunas: 2 })
          setConteudoSalvo(true)
        }
        if (r.capa) setCapa({ grafico: false, ...r.capa })
        setPasso(1)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pedidoExistente])

  // Conteúdo que o editor monta ao abrir. A importação por IA troca esta lista
  // e força o remount do editor (`versaoConteudo` é a key) — o humano revisa
  // linha a linha antes de salvar; a IA nunca grava nada sozinha.
  const [conteudoInicial, setConteudoInicial] = useState<Demonstracao[]>([])
  const [versaoConteudo, setVersaoConteudo] = useState(0)
  const [iaAberta, setIaAberta] = useState(false)
  const [textoColado, setTextoColado] = useState('')

  interface ResumoXml {
    codigoEntidade: string
    planoContas: string
    ano: number
    periodicidade: string
    mes: number | null
    trimestre: number | null
    emails: string[]
    lancamentos: number
    planosBeneficio: number
  }
  interface AuditoriaXml {
    ok: boolean
    totalLancamentos: number
    totalDebitosCentavos: number
    totalCreditosCentavos: number
    totalAtivoCentavos: number
    totalPassivoPlCentavos: number
    lucroLiquidoCentavos: number
    problemas: Array<{ regra: string; severidade: 'INFO' | 'WARNING' | 'CRITICAL'; mensagem: string }>
  }
  interface LancamentoXml {
    rubrica: string
    conta: string
    natureza: 'DV' | 'CR'
    debitoCentavos: number
    creditoCentavos: number
    saldoFinalCentavos: number
  }
  const [xmlInfo, setXmlInfo] = useState<{
    resumo: ResumoXml & { anoAnterior: number | null }
    auditoria: AuditoriaXml
    lancamentos: LancamentoXml[]
    arquivo: string
  } | null>(null)
  const [lendoXml, setLendoXml] = useState(false)
  const [ledgerAberto, setLedgerAberto] = useState(false)
  const [editorExpandido, setEditorExpandido] = useState(false)
  // Guardado para reprocessar junto quando o XML do exercício anterior chegar.
  const [arquivoAtual, setArquivoAtual] = useState<File | null>(null)
  const [arquivoAnterior, setArquivoAnterior] = useState<File | null>(null)

  const importarXml = useMutation({
    mutationFn: async ({ atual, anterior }: { atual: File; anterior: File | null }) => {
      const xml = await atual.text()
      const xmlAnterior = anterior ? await anterior.text() : undefined
      const r = await api.post<
        Item<{
          resumo: ResumoXml & { anoAnterior: number | null }
          auditoria: AuditoriaXml
          lancamentos: LancamentoXml[]
          demonstracoes: Demonstracao[]
        }>
      >(`/api/v1/pedidos/${pedido!.id}/balancete-xml`, { xml, xmlAnterior })
      return { r, nome: atual.name }
    },
    onSuccess: ({ r, nome }) => {
      setErro(null)
      setXmlInfo({
        resumo: r.dados.resumo,
        auditoria: r.dados.auditoria,
        lancamentos: r.dados.lancamentos,
        arquivo: nome,
      })
      if (r.dados.demonstracoes.length) {
        setConteudoInicial(r.dados.demonstracoes)
        setVersaoConteudo((v) => v + 1)
        setPrevia({ lista: r.dados.demonstracoes, colunas: 2 })
        setConteudoSalvo(false)
      }
      setLendoXml(false)
    },
    onError: (e: Error) => {
      setErro(e.message)
      setLendoXml(false)
    },
  })

  const subirLogo = useMutation({
    mutationFn: async (arquivo: File) => {
      const dataUri = await new Promise<string>((res, rej) => {
        const leitor = new FileReader()
        leitor.onload = () => res(String(leitor.result))
        leitor.onerror = rej
        leitor.readAsDataURL(arquivo)
      })
      return api.post<Item<{ logoUrl: string }>>(`/api/v1/organizacoes/${orgId}/logo`, { dataUri })
    },
    onSuccess: () => {
      setErro(null)
      void recarregarOrg()
    },
    onError: (e: Error) => setErro(e.message),
  })

  const gerarImagemCapa = useMutation({
    mutationFn: () =>
      api.post<Item<{ imagemUrl: string }>>(`/api/v1/pedidos/${pedido!.id}/capa-imagem`, {
        descricao: descricaoCapa,
      }),
    onSuccess: (r) => {
      setErro(null)
      setCapa((c) => ({ ...c, imagemUrl: r.dados.imagemUrl }))
      setCapaSalva(false)
    },
    onError: (e: Error) => setErro(e.message),
  })

  const salvarCapa = useMutation({
    mutationFn: () =>
      api.put<Item<unknown>>(`/api/v1/pedidos/${pedido!.id}/demonstracoes`, {
        demonstracoes: previa?.lista ?? [],
        colunas: previa?.colunas ?? 2,
        capa,
      }),
    onSuccess: () => {
      setErro(null)
      setCapaSalva(true)
      setConteudoSalvo(true)
    },
    onError: (e: Error) => setErro(e.message),
  })

  const gerarNotas = useMutation({
    mutationFn: () =>
      api.post<Item<{ texto: string }>>(`/api/v1/pedidos/${pedido!.id}/demonstracoes/notas`, {
        demonstracoes: previa?.lista ?? [],
      }),
    onSuccess: (r) => {
      setErro(null)
      const atual = previa?.lista ?? []
      const semNotas = atual.filter((d) => d.tipo !== 'notas_explicativas')
      const exercicio = semNotas[0]?.exercicio ?? new Date().getFullYear() - 1
      const novas: Demonstracao[] = [
        ...semNotas,
        { tipo: 'notas_explicativas', exercicio, exercicioComparativo: null, texto: r.dados.texto, linhas: [] },
      ]
      setConteudoInicial(novas)
      setVersaoConteudo((v) => v + 1)
      setPrevia({ lista: novas, colunas: previa?.colunas ?? 2 })
      setConteudoSalvo(false)
    },
    onError: (e: Error) => setErro(e.message),
  })

  const estruturar = useMutation({
    mutationFn: () =>
      api.post<Item<{ demonstracoes: Demonstracao[] }>>(
        `/api/v1/pedidos/${pedido!.id}/demonstracoes/estruturar`,
        { texto: textoColado },
      ),
    onSuccess: (r) => {
      setErro(null)
      setConteudoInicial(r.dados.demonstracoes)
      setVersaoConteudo((v) => v + 1)
      setPrevia({ lista: r.dados.demonstracoes, colunas: 2 })
      setConteudoSalvo(false)
      setIaAberta(false)
      setTextoColado('')
    },
    onError: (e: Error) => setErro(e.message),
  })

  const { data: categorias, isLoading } = useQuery({
    queryKey: ['categorias'],
    queryFn: () => api.get<Lista<Categoria>>('/api/v1/admin/categorias'),
  })

  const { data: orgs } = useQuery({
    queryKey: ['organizacoes'],
    queryFn: () => api.get<Lista<OrganizacaoResumo>>('/api/v1/organizacoes'),
    enabled: admin,
  })

  const categoria = categorias?.dados.find((c) => c.id === categoriaId) ?? null
  const eDemonstracoes = categoria?.formaIntake === 'demonstracoes_contabeis'

  // Detalhe da organização (traz a logomarca) — a mesma consulta para admin
  // (instituição escolhida) e cliente (a própria).
  const orgId = admin ? organizacaoId : (usuario?.organizacaoId ?? '')
  const { data: orgDetalhe, refetch: recarregarOrg } = useQuery({
    queryKey: ['organizacao-detalhe', orgId],
    queryFn: () => api.get<Item<OrganizacaoResumo>>(`/api/v1/organizacoes/${orgId}`),
    enabled: !!orgId,
  })
  const organizacao = orgDetalhe?.dados ?? null

  const criar = useMutation({
    mutationFn: () =>
      api.post<Item<PedidoCriado>>('/api/v1/pedidos', {
        categoriaId,
        organizacaoId: admin ? organizacaoId : undefined,
      }),
    onSuccess: async (r) => {
      setErro(null)
      setPedido(r.dados)
      if (eDemonstracoes) {
        // O servidor reaproveita rascunho aberto — e as demonstrações salvas
        // vivem em tabela própria, não no jsonb do pedido. Sem esta busca, um
        // rascunho retomado abriria o editor vazio por cima de conteúdo salvo.
        const salvas = await api
          .get<Lista<Demonstracao>>(`/api/v1/pedidos/${r.dados.id}/demonstracoes`)
          .then((d) => d.dados ?? [])
          .catch(() => [] as Demonstracao[])
        setConteudoInicial(salvas)
        setVersaoConteudo((v) => v + 1)
        if (salvas.length) {
          setPrevia({ lista: salvas, colunas: 2 })
          setConteudoSalvo(true)
        }
        setPasso(1)
      } else {
        // Planilha (escolas): o upload vive na ficha do pedido.
        navegar(`/pedidos?abrir=${r.dados.id}`)
      }
    },
    onError: (e: Error) => setErro(e.message),
  })

  const enviar = useMutation({
    mutationFn: async () => {
      // A transição usa lock otimista: manda a versão que acabou de ler. O
      // salvamento das demonstrações incrementa a versão, então ela é lida na
      // hora do envio, não guardada desde a criação.
      const atual = await api.get<Item<{ versao: number }>>(`/api/v1/pedidos/${pedido!.id}`)
      return api.post<Item<unknown>>(`/api/v1/pedidos/${pedido!.id}/transicao`, {
        para: 'em_validacao',
        versaoEsperada: atual.dados.versao,
        avisosCientes: true,
      })
    },
    onSuccess: () => navegar(`/pedidos?abrir=${pedido!.id}`),
    onError: (e: Error) => setErro(e.message),
  })

  if (isLoading) return <Carregando />

  const visiveis = categorias?.dados ?? []
  if (visiveis.length === 0) {
    return (
      <Vazio
        titulo="Nenhuma matéria disponível"
        descricao="Não há categoria de publicação ativa para o seu perfil."
        acao={
          <Link to="/pedidos" className="text-sm font-medium text-marca-700 hover:underline">
            ← Voltar aos pedidos
          </Link>
        }
      />
    )
  }

  const problemas = previa ? validarConjunto(previa.lista) : []
  const bloqueios = problemas.filter((p) => p.gravidade === 'rejeita')
  const estimativa = previa ? estimarComposicao(previa.lista, previa.colunas) : null

  return (
    <div className="space-y-5">
      {/* ------------------------------------------------------------ cabeçalho */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link to="/pedidos" className="text-xs font-medium text-marca-700 hover:underline">
            ← Pedidos
          </Link>
          <h1 className="mt-1 text-xl font-semibold tracking-tight text-slate-900">
            Nova publicação
          </h1>
        </div>
        <Stepper atual={passo} />
      </div>

      {erro && <Aviso tom="perigo">{erro}</Aviso>}

      {/* -------------------------------------------------------- passo 1: matéria */}
      {passo === 0 && (
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            {visiveis.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setCategoriaId(c.id)}
                className={`rounded-xl border-2 p-4 text-left transition ${
                  categoriaId === c.id
                    ? 'border-marca-600 bg-marca-50'
                    : 'border-[color:var(--color-borda)] bg-white hover:border-marca-300'
                }`}
              >
                <p className="font-medium text-slate-900">{c.nome}</p>
                {c.descricao && <p className="mt-1 text-sm text-slate-600">{c.descricao}</p>}
                {c.fundamentoLegal && (
                  <p className="mt-2 text-[11px] text-slate-500">{c.fundamentoLegal}</p>
                )}
              </button>
            ))}
          </div>

          {admin && (
            <Cartao titulo="Instituição">
              <Selecao
                rotulo="Em nome de qual instituição?"
                value={organizacaoId}
                onChange={(e) => setOrganizacaoId(e.target.value)}
                opcoes={[
                  { valor: '', texto: 'Selecione…' },
                  ...(orgs?.dados ?? []).map((o) => ({ valor: o.id, texto: o.razaoSocial })),
                ]}
                ajuda="A instituição precisa estar cadastrada. Cliente logado não vê este campo — o pedido nasce na organização dele."
              />
            </Cartao>
          )}

          <div className="flex justify-end">
            <Botao
              carregando={criar.isPending}
              disabled={!categoriaId || (admin && !organizacaoId)}
              onClick={() => criar.mutate()}
            >
              Continuar →
            </Botao>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------ passo 2: conteúdo */}
      {passo === 1 && pedido && (
        <div className="space-y-4">
          <div className="grid gap-5 lg:grid-cols-2">
            <Cartao
              titulo="Balancete XML (SICADI/Dataprev)"
              descricao="Envie o XML do balancete e as demonstrações são montadas na hora — determinístico, sem IA: os números vêm exatos do arquivo."
            >
              <label
                className={`flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed px-4 py-8 text-center transition ${
                  importarXml.isPending || lendoXml
                    ? 'border-marca-300 bg-marca-50'
                    : xmlInfo
                      ? 'border-emerald-300 bg-emerald-50'
                      : 'border-[color:var(--color-borda)] hover:border-marca-300 hover:bg-marca-50/40'
                }`}
              >
                <input
                  type="file"
                  accept=".xml,text/xml"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0]
                    if (f) {
                      setArquivoAtual(f)
                      setLendoXml(true)
                      importarXml.mutate({ atual: f, anterior: arquivoAnterior })
                    }
                    e.target.value = ''
                  }}
                />
                {importarXml.isPending || lendoXml ? (
                  <p className="text-sm font-medium text-marca-700">Lendo o balancete…</p>
                ) : xmlInfo ? (
                  <>
                    <p className="text-sm font-medium text-emerald-800">✓ {xmlInfo.arquivo}</p>
                    <p className="mt-1 text-xs text-slate-500">Clique para trocar o arquivo.</p>
                  </>
                ) : (
                  <>
                    <p className="text-sm font-medium text-slate-700">
                      Clique para escolher o arquivo .xml
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                      Formato SICADI/Dataprev (PC5) · até 20 MB
                    </p>
                  </>
                )}
              </label>
              {/* Exercício anterior: liga a comparação ano-a-ano real nas colunas. */}
              <label className="mt-3 flex cursor-pointer items-center justify-between gap-3 rounded-xl border border-[color:var(--color-borda)] px-4 py-3 transition hover:border-marca-300">
                <div>
                  <p className="text-sm font-medium text-slate-700">
                    XML do exercício anterior{' '}
                    <span className="font-normal text-slate-500">(opcional)</span>
                  </p>
                  <p className="text-xs text-slate-500">
                    {arquivoAnterior
                      ? `✓ ${arquivoAnterior.name} — comparação ano-a-ano real${xmlInfo?.resumo.anoAnterior ? ` (${xmlInfo.resumo.anoAnterior})` : ''}`
                      : 'Sem ele, a coluna comparativa usa o saldo inicial (aviso na auditoria).'}
                  </p>
                </div>
                <input
                  type="file"
                  accept=".xml,text/xml"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0]
                    if (f) {
                      setArquivoAnterior(f)
                      if (arquivoAtual) {
                        setLendoXml(true)
                        importarXml.mutate({ atual: arquivoAtual, anterior: f })
                      }
                    }
                    e.target.value = ''
                  }}
                />
                <span className="shrink-0 rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700">
                  {arquivoAnterior ? 'Trocar' : 'Escolher'}
                </span>
              </label>
            </Cartao>

            <Cartao
              titulo="Colar texto — a IA estrutura"
              descricao="Sem XML? Cole o balancete ou as demonstrações em texto (planilha ou PDF copiado). Nenhum valor é inventado — a IA só organiza o que está no texto."
              acoes={
                <Botao variante="secundario" tamanho="sm" onClick={() => setIaAberta((a) => !a)}>
                  {iaAberta ? 'Fechar' : 'Abrir'}
                </Botao>
              }
            >
              {iaAberta ? (
                <div className="space-y-3">
                  <AreaTexto
                    rotulo="Texto do balancete ou das demonstrações"
                    rows={6}
                    value={textoColado}
                    onChange={(e) => setTextoColado(e.target.value)}
                    placeholder={'ATIVO\n  Ativo Circulante ........ 1.234.567,89\n  Caixa e equivalentes .... 234.567,89\nPASSIVO\n  ...'}
                  />
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-xs text-slate-500">
                      O resultado substitui o editor abaixo — revise antes de salvar.
                    </p>
                    <Botao
                      carregando={estruturar.isPending}
                      disabled={textoColado.trim().length < 60}
                      onClick={() => estruturar.mutate()}
                    >
                      ✨ Estruturar com IA
                    </Botao>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-slate-500">
                  Também dá para digitar tudo manualmente no editor abaixo — importar é um atalho,
                  não uma obrigação.
                </p>
              )}
            </Cartao>
          </div>

          {xmlInfo && (
            <div className="space-y-4">
              {/* -------------------------------------- detectado no balancete */}
              <Cartao
                titulo="Detectado no balancete"
                descricao={`Retrato do arquivo — os números abaixo vêm exatos do XML, sem ajuste.`}
              >
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                  {[
                    { rotulo: 'Entidade', valor: xmlInfo.resumo.codigoEntidade || '—' },
                    { rotulo: 'Plano de contas', valor: xmlInfo.resumo.planoContas || '—' },
                    {
                      rotulo: 'Período',
                      valor:
                        xmlInfo.resumo.periodicidade === 'mensal'
                          ? `${String(xmlInfo.resumo.mes).padStart(2, '0')}/${xmlInfo.resumo.ano}`
                          : xmlInfo.resumo.periodicidade === 'trimestral'
                            ? `${xmlInfo.resumo.trimestre}º tri/${xmlInfo.resumo.ano}`
                            : String(xmlInfo.resumo.ano),
                    },
                    { rotulo: 'Lançamentos', valor: xmlInfo.resumo.lancamentos.toLocaleString('pt-BR') },
                    { rotulo: 'Planos de benefício', valor: String(xmlInfo.resumo.planosBeneficio) },
                    { rotulo: 'E-mails de contato', valor: xmlInfo.resumo.emails.length ? xmlInfo.resumo.emails.join(', ') : '—' },
                  ].map((c) => (
                    <div key={c.rotulo} className="rounded-xl border border-[color:var(--color-borda)] p-3">
                      <p className="text-[11px] font-medium text-slate-500">{c.rotulo}</p>
                      <p className="mt-0.5 truncate text-sm font-semibold text-slate-900" title={c.valor}>
                        {c.valor}
                      </p>
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {[
                    { rotulo: 'Total do Ativo', v: xmlInfo.auditoria.totalAtivoCentavos },
                    { rotulo: 'Passivo + PL', v: xmlInfo.auditoria.totalPassivoPlCentavos },
                    { rotulo: 'Lucro líquido', v: xmlInfo.auditoria.lucroLiquidoCentavos },
                  ].map((k) => (
                    <span key={k.rotulo} className="rounded-lg bg-marca-50 px-3 py-1.5 text-xs font-medium text-marca-800">
                      {k.rotulo}:{' '}
                      {(k.v / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </span>
                  ))}
                </div>
              </Cartao>

              {/* ------------------------------------------ auditoria contábil */}
              <Cartao
                titulo="Auditoria do balancete"
                descricao="As mesmas conferências que a EGBA faria na validação — melhor descobrir agora."
              >
                <div className="flex flex-wrap items-center gap-3">
                  <span
                    className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${
                      xmlInfo.auditoria.ok
                        ? 'bg-[color:var(--color-sucesso-fundo)] text-emerald-800'
                        : 'bg-red-50 text-red-800'
                    }`}
                  >
                    {xmlInfo.auditoria.ok ? '✓ Partidas dobradas fecham' : '✗ Há problemas críticos'}
                  </span>
                  <span className="text-xs text-slate-600">
                    Σ débitos{' '}
                    {(xmlInfo.auditoria.totalDebitosCentavos / 100).toLocaleString('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                    })}{' '}
                    · Σ créditos{' '}
                    {(xmlInfo.auditoria.totalCreditosCentavos / 100).toLocaleString('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                    })}
                  </span>
                </div>
                {xmlInfo.auditoria.problemas.length > 0 && (
                  <ul className="mt-3 space-y-1.5">
                    {xmlInfo.auditoria.problemas.map((p, i) => (
                      <li
                        key={i}
                        className={`rounded-lg px-3 py-2 text-xs leading-relaxed ${
                          p.severidade === 'CRITICAL'
                            ? 'bg-red-50 text-red-800'
                            : p.severidade === 'WARNING'
                              ? 'bg-[color:var(--color-atencao-fundo)] text-amber-800'
                              : 'bg-marca-50 text-marca-800'
                        }`}
                      >
                        {p.mensagem}
                      </li>
                    ))}
                  </ul>
                )}
                {/* Ledger completo — o "ver tudo" que dá confiança antes do envio. */}
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={() => setLedgerAberto((a) => !a)}
                    aria-expanded={ledgerAberto}
                    className="text-xs font-medium text-marca-700 underline decoration-dotted underline-offset-2 hover:text-marca-900"
                  >
                    {ledgerAberto
                      ? 'ocultar lançamentos'
                      : `ver os ${xmlInfo.lancamentos.length.toLocaleString('pt-BR')} lançamentos do consolidado`}
                  </button>
                  {ledgerAberto && (
                    <div className="mt-2 max-h-80 overflow-auto rounded-lg border border-[color:var(--color-borda)]">
                      <table className="w-full text-xs">
                        <thead className="sticky top-0 bg-slate-50">
                          <tr className="text-left text-slate-500">
                            <th className="px-3 py-2 font-medium">Rubrica</th>
                            <th className="px-3 py-2 font-medium">Conta</th>
                            <th className="px-3 py-2 font-medium">Nat.</th>
                            <th className="px-3 py-2 text-right font-medium">Débito</th>
                            <th className="px-3 py-2 text-right font-medium">Crédito</th>
                            <th className="px-3 py-2 text-right font-medium">Saldo final</th>
                          </tr>
                        </thead>
                        <tbody>
                          {xmlInfo.lancamentos.map((l) => (
                            <tr key={l.rubrica} className="border-t border-[color:var(--color-borda)]">
                              <td className="px-3 py-1.5 font-mono text-[11px] text-slate-500">{l.rubrica}</td>
                              <td className="px-3 py-1.5 text-slate-800">{l.conta}</td>
                              <td className="px-3 py-1.5 text-slate-500">{l.natureza}</td>
                              <td className="px-3 py-1.5 text-right tabular text-slate-700">
                                {(l.debitoCentavos / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                              </td>
                              <td className="px-3 py-1.5 text-right tabular text-slate-700">
                                {(l.creditoCentavos / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                              </td>
                              <td className="px-3 py-1.5 text-right tabular font-medium text-slate-900">
                                {(l.saldoFinalCentavos / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                <p className="mt-3 text-xs text-slate-500">
                  As demonstrações montadas do balancete já estão no editor abaixo — revise, ajuste
                  o que quiser e salve.
                </p>
              </Cartao>
            </div>
          )}

          <Cartao
            titulo="Identidade & capa"
            descricao="Logomarca no topo e rodapé da peça, capa com imagem gerada por IA e gráfico de destaque — tudo opcional, salvo com o rascunho."
            acoes={
              <Botao
                variante="secundario"
                tamanho="sm"
                carregando={salvarCapa.isPending}
                disabled={capaSalva}
                onClick={() => salvarCapa.mutate()}
              >
                {capaSalva ? 'Salvo' : 'Salvar identidade & capa'}
              </Botao>
            }
          >
            <div className="grid gap-4 lg:grid-cols-3">
              <div>
                <p className="text-xs font-medium text-slate-500">Logomarca da companhia</p>
                <label className="mt-1.5 flex cursor-pointer items-center gap-3 rounded-xl border border-[color:var(--color-borda)] px-3 py-2.5 transition hover:border-marca-300">
                  {organizacao?.logoUrl ? (
                    <img src={urlMidia(organizacao.logoUrl) ?? undefined} alt="" className="h-9 w-20 shrink-0 object-contain" />
                  ) : (
                    <span className="flex h-9 w-20 shrink-0 items-center justify-center rounded bg-slate-100 text-[10px] text-slate-400">
                      sem logo
                    </span>
                  )}
                  <span className="text-xs text-slate-600">
                    {subirLogo.isPending
                      ? 'Enviando…'
                      : organizacao?.logoUrl
                        ? 'Trocar (PNG/JPEG, até 2 MB)'
                        : 'Subir logomarca (PNG/JPEG, até 2 MB)'}
                  </span>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0]
                      if (f) subirLogo.mutate(f)
                      e.target.value = ''
                    }}
                  />
                </label>
                <p className="mt-1 text-[11px] text-slate-500">
                  Vai no topo e no rodapé de todas as publicações desta companhia.
                </p>
                <label className="mt-3 flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={!!capa.grafico}
                    onChange={(e) => {
                      setCapa((c) => ({ ...c, grafico: e.target.checked }))
                      setCapaSalva(false)
                    }}
                  />
                  Incluir gráfico de destaque (receita × lucro, desenhado dos próprios números)
                </label>
              </div>
              <div className="space-y-2">
                <AreaTexto
                  rotulo="Título da capa"
                  rows={1}
                  value={capa.titulo ?? ''}
                  onChange={(e) => {
                    setCapa((c) => ({ ...c, titulo: e.target.value }))
                    setCapaSalva(false)
                  }}
                  placeholder="Demonstrações Contábeis 2025"
                />
                <AreaTexto
                  rotulo="Subtítulo"
                  rows={1}
                  value={capa.subtitulo ?? ''}
                  onChange={(e) => {
                    setCapa((c) => ({ ...c, subtitulo: e.target.value }))
                    setCapaSalva(false)
                  }}
                  placeholder="Exercícios findos em 31 de dezembro de 2025 e 2024"
                />
              </div>
              <div className="space-y-2">
                <AreaTexto
                  rotulo="Imagem da capa — descreva e a IA gera"
                  rows={2}
                  value={descricaoCapa}
                  onChange={(e) => setDescricaoCapa(e.target.value)}
                  placeholder="ex.: vista aérea de um polo industrial na Bahia ao amanhecer, tons azuis"
                />
                <div className="flex items-center justify-between gap-2">
                  {capa.imagemUrl ? (
                    <img src={urlMidia(capa.imagemUrl) ?? undefined} alt="Capa" className="h-12 w-24 rounded object-cover" />
                  ) : (
                    <span className="text-[11px] text-slate-400">sem imagem</span>
                  )}
                  <div className="flex gap-2">
                    {capa.imagemUrl && (
                      <Botao
                        variante="secundario"
                        tamanho="sm"
                        onClick={() => {
                          setCapa((c) => ({ ...c, imagemUrl: null }))
                          setCapaSalva(false)
                        }}
                      >
                        Remover
                      </Botao>
                    )}
                    <Botao
                      tamanho="sm"
                      carregando={gerarImagemCapa.isPending}
                      disabled={descricaoCapa.trim().length < 15}
                      onClick={() => gerarImagemCapa.mutate()}
                    >
                      ✨ Gerar imagem
                    </Botao>
                  </div>
                </div>
              </div>
            </div>
          </Cartao>

          <div className="flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              disabled={gerarNotas.isPending || !(previa?.lista ?? []).some((d) => d.tipo !== 'notas_explicativas')}
              onClick={() => gerarNotas.mutate()}
              className="rounded-lg border border-[color:var(--color-borda)] px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-40"
              title="A IA redige um rascunho de notas usando só os números já digitados — fatos da companhia viram [REVISAR] para o contador preencher."
            >
              {gerarNotas.isPending ? 'Gerando notas…' : '✨ Rascunho de notas explicativas'}
            </button>
            <button
              type="button"
              onClick={() => setEditorExpandido((e) => !e)}
              className="rounded-lg border border-[color:var(--color-borda)] px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-50"
            >
              {editorExpandido ? '◫ Mostrar prévia ao lado' : '⛶ Expandir editor'}
            </button>
          </div>
          {/* Expandido: a prévia sobe para logo abaixo do botão — deixá-la no
              fim da página obrigava a rolar o editor inteiro para vê-la. */}
          <div className={`grid gap-5 ${editorExpandido ? '' : 'xl:grid-cols-2'}`}>
            <div className={`min-w-0 ${editorExpandido ? 'order-2' : ''}`}>
              <EditorDemonstracoes
                key={versaoConteudo}
                pedidoId={pedido.id}
                inicial={conteudoInicial}
                aoSalvar={() => setConteudoSalvo(true)}
                aoMudar={(lista, colunas) => {
                  setPrevia({ lista, colunas })
                  setConteudoSalvo(false)
                }}
              />
            </div>
            <div className={`min-w-0 ${editorExpandido ? 'order-1' : 'xl:sticky xl:top-4 xl:self-start'}`}>
              {previa && previa.lista.length > 0 ? (
                <PreviaDiario
                  cabecalho={{
                    razaoSocial: organizacao?.razaoSocial ?? '',
                    cnpj: organizacao?.cnpj ?? null,
                    logoUrl: organizacao?.logoUrl ?? null,
                  }}
                  demonstracoes={previa.lista}
                  colunas={previa.colunas}
                  capa={capa}
                />
              ) : (
                <Cartao titulo="Prévia diagramada">
                  <p className="text-sm text-slate-500">
                    Comece a preencher as demonstrações ao lado — a prévia no padrão do Diário
                    aparece aqui, com régua de centímetros reais.
                  </p>
                </Cartao>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-500">
              {conteudoSalvo
                ? 'Conteúdo salvo no rascunho.'
                : 'Alterações não salvas — use “Salvar” no editor antes de avançar.'}
            </p>
            <div className="flex gap-3">
              <Botao variante="secundario" onClick={() => navegar('/pedidos')}>
                Salvar rascunho e sair
              </Botao>
              <Botao disabled={!conteudoSalvo || bloqueios.length > 0} onClick={() => setPasso(2)}>
                Revisar →
              </Botao>
            </div>
          </div>
        </div>
      )}

      {/* ------------------------------------------------- passo 3: revisão e envio */}
      {passo === 2 && pedido && previa && (
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <Cartao titulo="Matéria">
              <p className="text-sm font-medium text-slate-900">{categoria?.nome}</p>
              <p className="mt-1 text-xs text-slate-500">
                {organizacao?.razaoSocial ?? 'Sua instituição'}
              </p>
              {pedido.protocolo && (
                <p className="mt-1 text-xs text-slate-500">Protocolo {pedido.protocolo}</p>
              )}
            </Cartao>
            <Cartao titulo="Composição estimada">
              {estimativa && (
                <>
                  <p className="text-2xl font-semibold tracking-tight text-slate-900">
                    ≈ {estimativa.alturaCm} cm
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    {estimativa.linhasTotais} linhas em {estimativa.colunas} coluna
                    {estimativa.colunas > 1 ? 's' : ''} (~{estimativa.alturaPorColunaCm} cm por
                    coluna)
                  </p>
                  <span className="mt-2 inline-block rounded bg-[color:var(--color-atencao-fundo)] px-1.5 py-0.5 text-[10px] font-medium text-amber-800">
                    estimativa — o valor sai da medição oficial
                  </span>
                </>
              )}
            </Cartao>
            <Cartao titulo="Conferência">
              {problemas.length === 0 ? (
                <p className="text-sm text-emerald-700">Nenhum problema encontrado.</p>
              ) : (
                <ul className="space-y-1 text-xs">
                  {problemas.slice(0, 5).map((p, i) => (
                    <li
                      key={i}
                      className={p.gravidade === 'rejeita' ? 'text-red-700' : 'text-amber-700'}
                    >
                      {p.mensagem}
                    </li>
                  ))}
                </ul>
              )}
            </Cartao>
          </div>

          <PreviaDiario
            cabecalho={{
              razaoSocial: organizacao?.razaoSocial ?? '',
              cnpj: organizacao?.cnpj ?? null,
              logoUrl: organizacao?.logoUrl ?? null,
            }}
            demonstracoes={previa.lista}
            colunas={previa.colunas}
            capa={capa}
          />

          <div className="flex items-center justify-between">
            <Botao variante="secundario" onClick={() => setPasso(1)}>
              ← Voltar ao conteúdo
            </Botao>
            <div className="flex gap-3">
              <Botao variante="secundario" onClick={() => navegar('/pedidos')}>
                Salvar rascunho e sair
              </Botao>
              <Botao
                carregando={enviar.isPending}
                disabled={bloqueios.length > 0}
                onClick={() => enviar.mutate()}
              >
                Enviar para análise da EGBA
              </Botao>
            </div>
          </div>
          <p className="text-right text-xs text-slate-500">
            Depois do envio o conteúdo fica congelado — a EGBA valida, mede e devolve o valor para
            sua aprovação.
          </p>
        </div>
      )}
    </div>
  )
}
